import { useMemo, useState } from 'react';
import type { MenuNote } from '../../shared/menu';

interface NotesBannerProps {
  notes: MenuNote[];
  // Stable key for per-session dismissal. Use the menuId — different menus
  // have different notes, so a dismiss on one shouldn't silence the next.
  dismissKey?: string;
}

// Tiered menu-level notes:
// - pricing → always visible, red-accent (wallet-critical)
// - safety  → always visible, amber (dietary / religious)
// - info    → collapsed by default, neutral (prix-fixe, hours)
// - parse   → tiny quiet footer ("Some items may be missing.")
export function NotesBanner({ notes, dismissKey }: NotesBannerProps) {
  const pricing = useMemo(() => notes.filter((n) => n.severity === 'pricing'), [notes]);
  const safety = useMemo(() => notes.filter((n) => n.severity === 'safety'), [notes]);
  const info = useMemo(() => notes.filter((n) => n.severity === 'info'), [notes]);
  const parse = useMemo(() => notes.filter((n) => n.severity === 'parse'), [notes]);

  const storageKey = dismissKey ? `mtm:notes-dismissed:${dismissKey}` : null;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!storageKey || typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(storageKey) === '1';
  });
  const [infoExpanded, setInfoExpanded] = useState(false);

  if (dismissed) return null;
  if (notes.length === 0) return null;

  const dismiss = () => {
    if (storageKey && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(storageKey, '1');
    }
    setDismissed(true);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
      {pricing.length > 0 && (
        <Block
          items={pricing}
          accent="#C4332B"
          bg="rgba(196,51,43,0.08)"
          label="Pricing"
          onDismiss={dismiss}
        />
      )}
      {safety.length > 0 && (
        <Block
          items={safety}
          accent="#B98300"
          bg="rgba(185,131,0,0.08)"
          label="Good to know"
          onDismiss={dismiss}
        />
      )}
      {info.length > 0 && (
        <div
          style={{
            borderRadius: 10,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-divider)',
            padding: '10px 14px',
            fontSize: 13,
          }}
        >
          {infoExpanded ? (
            <>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {info.map((n, i) => (
                  <li key={i} style={{ color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
                    · {n.text}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setInfoExpanded(false)}
                style={linkButton()}
              >
                Hide
              </button>
            </>
          ) : (
            <button
              onClick={() => setInfoExpanded(true)}
              style={{ ...linkButton(), padding: 0 }}
            >
              Show {info.length} menu note{info.length === 1 ? '' : 's'}
            </button>
          )}
        </div>
      )}
      {parse.length > 0 && (
        <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '2px 2px 0', fontStyle: 'italic' }}>
          {parse.map((n) => n.text).join(' · ')}
        </p>
      )}
    </div>
  );
}

function Block({
  items,
  accent,
  bg,
  label,
  onDismiss,
}: {
  items: MenuNote[];
  accent: string;
  bg: string;
  label: string;
  onDismiss: () => void;
}) {
  return (
    <div
      role="note"
      style={{
        borderRadius: 10,
        background: bg,
        border: `1px solid ${accent}33`,
        padding: '10px 14px',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 700,
            color: accent,
            marginBottom: 4,
          }}
        >
          {label}
        </div>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {items.map((n, i) => (
            <li key={i} style={{ fontSize: 14, lineHeight: 1.4, color: 'var(--color-primary)' }}>
              {n.text}
            </li>
          ))}
        </ul>
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--color-text-secondary)',
          padding: 4,
          margin: '-4px -4px 0 0',
          fontSize: 18,
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        ×
      </button>
    </div>
  );
}

function linkButton(): React.CSSProperties {
  return {
    background: 'transparent',
    border: 'none',
    color: 'var(--color-accent)',
    fontSize: 13,
    cursor: 'pointer',
    padding: '4px 0 0',
    fontWeight: 500,
  };
}
